import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemToken } from 'designSystem/types';
import * as S from './styles';

interface LineHeightScaleProps {
	tokens: DesignSystemToken[];
	dark: boolean;
	primaryFont: string;
}

const SAMPLE_TEXT =
	'Line height controls the vertical rhythm of your text. This sample shows how multiple lines of body text breathe with different leading values.';

const getLineHeight = (token: DesignSystemToken): number => {
	if (token.value.kind === 'lineHeight') {
		if (token.value.numeric !== undefined) return token.value.numeric;
		if (token.value.length?.px) return token.value.length.px / 16;
	}
	return 1.5;
};

const formatLineHeight = (token: DesignSystemToken): string => {
	if (token.value.kind === 'lineHeight') {
		if (token.value.numeric !== undefined) return String(token.value.numeric);
		return token.value.original;
	}
	return token.value.original;
};

const LineHeightScale = ({ tokens, dark, primaryFont }: LineHeightScaleProps) => {
	const dispatch = useDispatch();
	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const sorted = useMemo(
		() => [...tokens].sort((a, b) => getLineHeight(a) - getLineHeight(b)),
		[tokens]
	);

	const isSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	if (sorted.length === 0) return null;

	return (
		<S.LineHeightGrid>
			{sorted.map((token) => {
				const lh = getLineHeight(token);
				return (
					<S.LineHeightItem
						key={token.id}
						type="button"
						$selected={isSelected(token)}
						$dark={dark}
						onClick={() =>
							dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }))
						}
					>
						<S.LineHeightTooltip>{token.cssVariable}</S.LineHeightTooltip>
						<S.LineHeightSample $dark={dark} style={{ lineHeight: lh, fontFamily: primaryFont }}>
							{SAMPLE_TEXT}
						</S.LineHeightSample>
						<S.LineHeightLabel>{token.name}</S.LineHeightLabel>
						<S.LineHeightValue>{formatLineHeight(token)}</S.LineHeightValue>
					</S.LineHeightItem>
				);
			})}
		</S.LineHeightGrid>
	);
};

export default LineHeightScale;
